import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { AuthService } from './auth.service';
import { ChatService } from './chat.service';

@Injectable({
  providedIn: 'root',
})
export class ChatNotificationService {
  private hasNewMessageMap: { [groupId: string]: boolean } = {};
  private hasNewMessageChatMap: { [groupId: string]: boolean } = {};
  private hasNewMessagePttMap: { [groupId: string]: boolean } = {};
  private unreadCountMap: { [groupId: string]: number } = {};
  private unreadCountChatMap: { [groupId: string]: number } = {};
  private unreadCountPttMap: { [groupId: string]: number } = {};
  private unreadCount: { [groupId: string]: number } = {};
  private unreadChatCount: { [groupId: string]: number } = {};
  private unreadPttCount: { [groupId: string]: number } = {};

  private subs: { [groupId: string]: Subscription } = {};
  public hasNewGroupMessage$ = new BehaviorSubject<{
    [groupId: string]: boolean;
  }>({});

  public hasNewMessageChat$ = new BehaviorSubject<{
    [groupId: string]: boolean;
  }>({});

  public hasNewMessagePtt$ = new BehaviorSubject<{
    [groupId: string]: boolean;
  }>({});

  public unreadGroupCount$ = new BehaviorSubject<{ [groupId: string]: number }>(
    {}
  );

  public unreadChatCount$ = new BehaviorSubject<{ [groupId: string]: number }>(
    {}
  );

  public unreadPttCount$ = new BehaviorSubject<{ [groupId: string]: number }>(
    {}
  );

  private currentUserId!: any;
  private listenedGroupIds = new Set<string>();
  private listenedChatIds = new Set<string>();
  private listenedPttGroupIds = new Set<string>();

  constructor(
    private authService: AuthService,
    private chatService: ChatService
  ) {}

  // Safe to call repeatedly (e.g. on every chat-page entry/poll): each
  // group/chat only gets one Firestore listener, but re-scanning picks up
  // brand-new conversations that started after the previous call.
  async init() {
    const currentUser = await this.authService.getCurrentUserValue();
    this.currentUserId = currentUser.user_id;

    const groups = await this.chatService.getAllGroupsWithouLoading().toPromise();
    if (!!groups || groups.length) {
      for (const group of groups) {
        this.listenToGroup(group.id);

        this.listenPttGroup(group.id);
      }
    }

    const currentChats = await firstValueFrom(
      this.chatService.getConnectedUsers()
    );
    if (!!currentChats && !!currentChats.length) {
      for (const chat of currentChats) {
        const chatId = this.chatService.getChatId(
          chat.fromUserId,
          chat.toUserId
        );
        this.listenChat(chatId);
      }
    }
  }

  async initGroupNotifications(groupIds: string[]) {
    const currentUserId: any = (await this.authService.getCurrentUserValue())
      .user_id;

    groupIds.forEach((groupId) => {
      if (this.subs[groupId]) return;

      this.subs[groupId] = this.chatService
        .listenGroupMessages(groupId)
        .subscribe((msgs: any[]) => {
          const unreadMsgs = msgs.filter(
            (m) => !m.readBy?.includes(currentUserId)
          );
          this.unreadCount[groupId] = unreadMsgs.length;
          if (unreadMsgs.length > 0) {
            this.hasNewMessageMap[groupId] = unreadMsgs.length > 0;
          } else {
            delete this.hasNewMessageMap[groupId];
          }
        });
    });
  }

  private async listenToGroup(groupId: string) {
    if (this.listenedGroupIds.has(groupId)) return;
    this.listenedGroupIds.add(groupId);

    this.chatService.listenGroupMessages(groupId).subscribe((msgs: any[]) => {
      if (!msgs.length) {
        if (this.hasNewMessageMap[groupId]) {
          delete this.hasNewMessageMap[groupId];
        }
        this.unreadCountMap[groupId] = 0;
      } else {
        const unreadMsgs = msgs.filter(
          (m) => !m.readBy?.includes(this.currentUserId)
        );
        if (unreadMsgs.length > 0) {
          this.hasNewMessageMap[groupId] = unreadMsgs.length > 0;
        } else {
          delete this.hasNewMessageMap[groupId];
        }

        this.unreadCountMap[groupId] = unreadMsgs.length;
      }

      this.hasNewGroupMessage$.next({ ...this.hasNewMessageMap });
      this.unreadGroupCount$.next({ ...this.unreadCountMap });
    });
  }

  private async listenChat(chatId: string) {
    if (this.listenedChatIds.has(chatId)) return;
    this.listenedChatIds.add(chatId);

    this.chatService.listenChatMessages(chatId).subscribe((msgs: any[]) => {
      if (!msgs.length) {
        if (this.hasNewMessageChatMap[chatId]) {
          delete this.hasNewMessageChatMap[chatId];
        }
        this.unreadCountChatMap[chatId] = 0;
      } else {
        const unreadMsgs = msgs.filter(
          (m) => !m.readBy?.includes(this.currentUserId)
        );
        if (unreadMsgs.length > 0) {
          this.hasNewMessageChatMap[chatId] = unreadMsgs.length > 0;
        } else {
          delete this.hasNewMessageChatMap[chatId];
        }

        this.unreadCountChatMap[chatId] = unreadMsgs.length;
      }

      this.hasNewMessageChat$.next({ ...this.hasNewMessageChatMap });
      this.unreadChatCount$.next({ ...this.unreadCountChatMap });
    });
  }

  private async listenPttGroup(groupId: string) {
    if (this.listenedPttGroupIds.has(groupId)) return;
    this.listenedPttGroupIds.add(groupId);

    this.chatService.listenPttGroupMessage(groupId).subscribe((msgs: any[]) => {
      if (!msgs.length) {
        if (this.hasNewMessagePttMap[groupId]) {
          delete this.hasNewMessagePttMap[groupId];
        }

        this.unreadCountPttMap[groupId] = 0;
      } else {
        const unreadMsgs = msgs.filter(
          (m) => !m.readBy?.includes(this.currentUserId)
        );
        if (unreadMsgs.length > 0) {
          this.hasNewMessagePttMap[groupId] = unreadMsgs.length > 0;
        } else {
          delete this.hasNewMessagePttMap[groupId];
        }

        this.unreadCountPttMap[groupId] = unreadMsgs.length;

        this.hasNewMessagePtt$.next({ ...this.hasNewMessagePttMap });
        this.unreadPttCount$.next({ ...this.unreadCountPttMap });
      }
    });
  }

  markAsRead(groupId: string) {
    this.hasNewMessageMap[groupId] = false;
    this.unreadCountMap[groupId] = 0;
    this.hasNewGroupMessage$.next({ ...this.hasNewMessageMap });
    this.unreadGroupCount$.next({ ...this.unreadCountMap });
  }
}
